import { useEffect, useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import { ExtendedSidebar } from '@/components/shared/extendedSidebar/ExtendedSidebar';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';
import { Header } from '@/components/shared/header/Header';
import useWebSocket from '@/hooks/useWebsocket';
import { NotificationContent } from './NotificationContent';
import useNotificationState from '@/states/notification.state';
import useTaskState from '@/states/task.state';
import { ToastContainer, toast } from 'react-toastify';
import { ExtendedMenuConfig } from '@/utils/configs/menu';
import { useWindowSize } from '@/hooks/useWindowSize';
import { BREAKPOINTS } from '@/utils/constants/breakpoints';
import { useTenantWatcher } from '@/hooks/useTenantWatcher';
import useTenantSettingState from '@/states/tenant-setting.state';
import { ThemeProvider } from '@/components/theme/ThemeContext';
import { env } from '@/utils/constants/env';
import { RefreshCw } from 'lucide-react';

const MenuWrapper = (props: { children: JSX.Element; menu: ExtendedMenuConfig }) => {
  const { children, menu } = props;

  if (menu.type === 'label') {
    const percentage = typeof menu.percentage === 'number' ? Math.min(100, Math.max(0, menu.percentage)) : null;

    return (
      <div className="px-6 py-2 text-xs text-gray-500 font-bold tracking-wider">
        <div className="mb-1">{menu.label}</div>

        {percentage !== null && (
          <div className="flex items-center gap-2">
            <div className="relative flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-primary transition-all"
                style={{ width: `${percentage}%` }}
              />
            </div>

            <span className="text-[10px] font-medium text-gray-500">{percentage}%</span>
          </div>
        )}
      </div>
    );
  }

  if (menu.onClick) {
    return (
      <button onClick={menu.onClick} className="w-full text-left">
        {children}
      </button>
    );
  }

  return (
    <Link to={menu.href ?? '#'} target={menu.newPage ? '_blank' : '_self'}>
      {children}
    </Link>
  );
};

type AppType = 'studio' | 'app' | 'companyProfile';

export const AdminLayout = () => {
  const { userProfile } = useAuthState();
  const { logout } = useAuthState();
  const { removeCurrentTenant, currentTenant } = useTenantState();
  const { fetchTaskCount, fetchTaskGroupCount } = useTaskState();
  const { getTenantTheme } = useTenantSettingState();
  const { width } = useWindowSize();
  const [toggled, setToggled] = useState(width < BREAKPOINTS.MD);
  const activeApp = 'companyProfile' as AppType;
  useTenantWatcher();

  const { currentTheme } = useTenantSettingState();

  useEffect(() => {
    setToggled(width < BREAKPOINTS.MD);
  }, [width]);

  useEffect(() => {
    if (currentTenant) {
      fetchTaskCount();
      fetchTaskGroupCount();
      getTenantTheme(currentTenant.id);
    }
  }, [currentTenant?.id]);

  const { notificationList: notifications, notificationCount, fetchNotifications } = useNotificationState();

  const userData = {
    username: userProfile?.email,
  };

  useWebSocket(
    userData,
    (notif: any) => {
      if (userData.username) {
        fetchNotifications(userData.username);
        fetchTaskCount();
        fetchTaskGroupCount();
        const data = JSON.parse(notif);
        toast.success(`${data.subject}`);
      }
    },
    () => {
      return;
    },
  );

  useEffect(() => {
    if (userData.username) {
      fetchNotifications(userData.username);
    }
  }, [userData.username]);

  const handleLogout = () => {
    removeCurrentTenant();
    logout();
  };
  const AvatarContent = () => (
    <div className="p-2">
      <button
        className="w-full p-2 text-left rounded cursor-pointer hover:bg-gray-100 "
        onClick={() => {
          removeCurrentTenant();
          logout();
        }}
      >
        Logout
      </button>
    </div>
  );

  return (
    <ThemeProvider>
      <div className="max-w-screen min-h-screen  ">
        <div className="sticky top-0 z-10">
          <Header
            toggled={toggled}
            setToggled={setToggled}
            avatarContent={<AvatarContent />}
            notificationContent={<NotificationContent notifications={notifications} />}
            notificationCount={notificationCount}
            rightContent={
              <div
                className={twJoin(
                  "flex items-center h-full select-none font-medium text-sm",
                  currentTheme?.headerContentClass
                )}
              >
                {/* STUDIO */}
                <button
                  type="button"
                  onClick={() => {
                    globalThis.location.href = `${env.STUDIO_URL}/${currentTenant?.slug}`;
                  }}
                  className={`cursor-pointer px-3 py-2 border-b-4 flex items-center gap-2 ${
                    activeApp === 'studio'
                      ? currentTheme?.headerActiveItemClass || 'border-blue-500 text-blue-500'
                      : 'border-transparent hover:bg-gray-100'
                  }`}
                >
                  Studio
                </button>

                {/* APP */}
                <button
                  type="button"
                  onClick={() => {
                    globalThis.location.href = `${env.APP_URL}/${currentTenant?.slug}`;
                  }}
                  className={`cursor-pointer px-3 py-2 border-b-4 flex items-center gap-2 ${
                    activeApp === 'app'
                      ? currentTheme?.headerActiveItemClass || 'border-blue-500 text-blue-500'
                      : 'border-transparent hover:bg-gray-100'
                  }`}
                >
                  App
                </button>

                {/* COMPRO */}
                <div
                  className={`px-3 py-2 border-b-4 flex items-center gap-2 cursor-default ${
                    activeApp === 'companyProfile'
                      ? currentTheme?.headerActiveItemClass || 'border-blue-500 text-blue-500'
                      : 'border-transparent'
                  }`}
                >
                  Company Profile
                </div>
              </div>
            }
          />
        </div>
        <ExtendedSidebar toggled={toggled} setToggled={setToggled} menuWrapper={MenuWrapper} />

        <div
          className={twJoin(
            'transition-[margin] ease-in-out duration-400 ',
            toggled ? 'md:ml-[80px]' : 'md:ml-[238px]',
          )}
        >
          <main className="flex flex-col items-center">
            <Outlet key={currentTenant?.id} />
          </main>
        </div>

        <ToastContainer />
      </div>
    </ThemeProvider>
  );
};
